import PlusIcon from "../../../assets/icons/plus.svg?react";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { InstancesBackupsUrl, InstancesCreateUrl } from "../../../utils/urls";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { DeleteModal } from "../../modals/QuestionModal";
import ArchiveIcon from "./../../../assets/icons/archive-box.svg?react";
import { useSearch } from "../../../providers/SearchProvider";
import {
  deleteInstancesSnapshotAsync,
  getInstancesAsync,
  getInstancesSnapshotsAsync,
  selectInstances,
  selectInstancesLoading,
  selectInstancesSnapshots,
  selectInstancesSnapshotsActionLoading,
  selectInstancesSnapshotsLoading,
} from "../../../store/instances/instancesSlice";
import MoreIcon from "./../../../assets/icons/more.svg?react";
import { InstanceSnapshot } from "../../../types/instance";
import {
  Alert,
  Button,
  Dropdown,
  EmptyState,
  Flex,
  SimpleTable,
  Tag,
  Typography,
} from "djuno-design";
import HighlighterText from "../../general/HighlighterText";

const InstancesBackupsTab = () => {
  const [deleteSnapshot, setDeleteSnapshot] = useState<InstanceSnapshot | null>(
    null
  );

  const instances = useAppSelector(selectInstances);
  const loading = useAppSelector(selectInstancesLoading);

  const snapshots = useAppSelector(selectInstancesSnapshots);
  const snapshotsLoading = useAppSelector(selectInstancesSnapshotsLoading);
  const snapshotsActionLoading = useAppSelector(
    selectInstancesSnapshotsActionLoading
  );

  const [filteredSnapshots, setFilteredSnapshots] = useState<
    InstanceSnapshot[]
  >([]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { value: searchValue } = useSearch();

  useEffect(() => {
    if (!instances) {
      dispatch(getInstancesAsync({ withoutLoading: false }));
    }
  }, [dispatch, instances]);

  useEffect(() => {
    dispatch(getInstancesSnapshotsAsync({}));
  }, [dispatch]);

  useEffect(() => {
    if (snapshots) {
      const filtered = snapshots.filter((snapshot) =>
        snapshot.name.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredSnapshots(filtered);
    }
  }, [searchValue, snapshots]);

  return (
    <div>
      {instances && (
        <div className="mt-2 flex flex-col gap-4 w-full">
          <div className="mt-5 w-full">
            <SimpleTable
              loading={snapshotsLoading}
              containerClassName="min-h-[240px]"
            >
              <SimpleTable.Head>
                <SimpleTable.Row>
                  <SimpleTable.TH lable="Name" />
                  <SimpleTable.TH lable="ID" />
                  <SimpleTable.TH lable="Region" />
                  <SimpleTable.TH lable="Size" />
                  <SimpleTable.TH lable="Creation date" />
                  <SimpleTable.TH lable="Status" />
                  <SimpleTable.TH lable="" />
                </SimpleTable.Row>
              </SimpleTable.Head>
              <SimpleTable.Body>
                {filteredSnapshots.map((snapshot, i) => (
                  <SimpleTable.Row key={i}>
                    <SimpleTable.TD>
                      <div className="flex flex-col gap-1">
                        <HighlighterText
                          searchValue={searchValue}
                          textToHighlight={snapshot.name}
                          className="max-w-[110px] md:max-w-[400px] lg:max-w-[350px] xl:max-w-[400px] truncate"
                        />
                      </div>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Text className="!text-xs md:!text-sm !whitespace-nowrap">
                        {snapshot.id}
                      </Typography.Text>
                    </SimpleTable.TD>

                    <SimpleTable.TD>
                      <Typography.Text className="!text-xs md:!text-sm !whitespace-nowrap">
                        {snapshot.region}
                      </Typography.Text>
                    </SimpleTable.TD>

                    <SimpleTable.TD>
                      <Typography.Text className="!text-xs md:!text-sm !whitespace-nowrap">
                        {snapshot.size.toFixed(2)} GiB
                      </Typography.Text>
                    </SimpleTable.TD>

                    <SimpleTable.TD>
                      <Typography.Text className="!text-xs md:!text-sm !whitespace-nowrap">
                        {snapshot.creationDate}
                      </Typography.Text>
                    </SimpleTable.TD>

                    <SimpleTable.TD>
                      <Flex>
                        <Tag
                          // className="text-xs whitespace-nowrap"
                          color={
                            snapshot.status === "active" ? "success" : "warning"
                          }
                        >
                          {snapshot.status}
                        </Tag>
                      </Flex>
                    </SimpleTable.TD>

                    <SimpleTable.TD className="w-20 gap-1">
                      <div className="h-full w-full inline-flex items-center justify-end gap-1">
                        <div className="w-8 flex justify-center items-center">
                          <Dropdown
                            itemsClassName="!p-0"
                            anchor="bottom end"
                            menu={[
                              {
                                key: "1",
                                label: (
                                  <div className="flex items-center gap-1">
                                    {/* <DetailsIcon className="w-4" /> */}
                                    <PlusIcon className="w-4" />
                                    Create instance
                                  </div>
                                ),
                                onClick: () =>
                                  navigate(
                                    `${InstancesCreateUrl}?instanceBackupId=${snapshot.id}&instanceId=${snapshot.instanceId}&returnUrl=${InstancesBackupsUrl}`
                                  ),
                                disabled:
                                  loading || snapshot.status !== "active",
                              },
                              {
                                type: "divider",
                              },
                              {
                                key: "end",
                                label: (
                                  <div className="flex items-center gap-1">
                                    <ArchiveIcon className="w-4" />
                                    Delete
                                  </div>
                                ),
                                danger: true,
                                onClick: (_, close) => {
                                  close();
                                  setDeleteSnapshot(snapshot);
                                },
                                disabled:
                                  loading || snapshot.status !== "active",
                              },
                            ]}
                          >
                            <Button
                              uiType="icon"
                              uiSize="small"
                              className="!px-2"
                            >
                              <MoreIcon className="w-4 h-4" />
                            </Button>
                          </Dropdown>
                        </div>
                        {/* <ArrowRightIcon className="w-4 h-4 dark:text-slate-100 text-slate-800 cursor-pointer" /> */}
                      </div>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                ))}

                {filteredSnapshots.length === 0 && (
                  <SimpleTable.Row
                    withoutHoverStyle={true}
                    className="h-[200px]"
                  >
                    <SimpleTable.TD colSpan={9} className="!border-0">
                      <EmptyState text="You have not created any backup yet" />
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                )}
              </SimpleTable.Body>
            </SimpleTable>
          </div>
        </div>
      )}

      <DeleteModal
        title="Delete a Backup"
        description={
          <div className="flex flex-col">
            <Typography.Text className="mb-5">
              Permanently delete
              {deleteSnapshot?.name} {deleteSnapshot?.creationDate}
            </Typography.Text>
            <Alert
              uiType="warning"
              className="text-xs mb-3"
              // icon={<ErrorIcon className="w-5 mr-1 aspect-square" />}
            >
              All of the data on this instance backup will be lost.
            </Alert>
          </div>
        }
        isOpen={deleteSnapshot !== null}
        onClose={() => setDeleteSnapshot(null)}
        confirmButtonText="Confirm"
        confirmButtonType="danger"
        confirmButtonClassName="w-full"
        onConfirm={async () => {
          if (!deleteSnapshot) return;

          const action = await dispatch(
            deleteInstancesSnapshotAsync({
              imageId: deleteSnapshot.id,
            })
          );

          if (action.type === "instances/snapshot/delete/fulfilled") {
            await dispatch(getInstancesSnapshotsAsync({}));

            setDeleteSnapshot(null);
          }
        }}
        loading={snapshotsActionLoading}
        confirmString={deleteSnapshot?.name}
      />
    </div>
  );
};

export default InstancesBackupsTab;
